// Testimonial Service - Firebase Firestore for Testimonials
import {
    collection,
    doc,
    getDocs,
    getDoc,
    addDoc,
    updateDoc,
    deleteDoc,
    query,
    orderBy,
    serverTimestamp,
    Timestamp
} from 'firebase/firestore';
import { db } from './firebase';
import { Testimonial } from '../types';

const TESTIMONIALS_COLLECTION = 'testimonials';
const TESTIMONIALS_STORAGE_KEY = 'nescotech_testimonials';

// LocalStorage fallback helpers
const getLocalTestimonials = (): Testimonial[] => {
    try {
        const stored = localStorage.getItem(TESTIMONIALS_STORAGE_KEY);
        return stored ? JSON.parse(stored) : [];
    } catch { return []; }
};

const saveLocalTestimonials = (testimonials: Testimonial[]) => {
    localStorage.setItem(TESTIMONIALS_STORAGE_KEY, JSON.stringify(testimonials));
};

// ==================== TESTIMONIALS CRUD ====================

// Get all testimonials
export const getTestimonials = async (): Promise<Testimonial[]> => {
    try {
        console.log('📡 Fetching testimonials from Firebase...');
        const testimonialsRef = collection(db, TESTIMONIALS_COLLECTION);
        const snapshot = await getDocs(testimonialsRef);

        console.log('📦 Found', snapshot.docs.length, 'testimonials in Firebase');

        const testimonials = snapshot.docs.map(doc => ({
            id: doc.id,
            ...doc.data()
        })) as Testimonial[];

        // Sort by date client-side (newest first)
        testimonials.sort((a: any, b: any) => {
            const dateA = new Date(a.date || a.createdAt?.toDate?.() || 0);
            const dateB = new Date(b.date || b.createdAt?.toDate?.() || 0);
            return dateB.getTime() - dateA.getTime();
        });

        return testimonials;
    } catch (error) {
        console.error('❌ Firebase error, using localStorage:', error);
        return getLocalTestimonials();
    }
};

// Get single testimonial by ID
export const getTestimonialById = async (id: string): Promise<Testimonial | null> => {
    try {
        const docRef = doc(db, TESTIMONIALS_COLLECTION, id);
        const docSnap = await getDoc(docRef);

        if (docSnap.exists()) {
            return {
                id: docSnap.id,
                ...docSnap.data()
            } as Testimonial;
        }

        return null;
    } catch (error) {
        console.error('❌ Error fetching testimonial:', error);
        return null;
    }
};

// Get approved testimonials only
export const getApprovedTestimonials = async (): Promise<Testimonial[]> => {
    try {
        const allTestimonials = await getTestimonials();
        return allTestimonials.filter(t => t.status === 'approved' || t.status === 'active');
    } catch (error) {
        console.error('❌ Error fetching approved testimonials:', error);
        return [];
    }
};

// Add new testimonial
export const addTestimonial = async (testimonialData: Partial<Testimonial>): Promise<string | null> => {
    try {
        const testimonialsRef = collection(db, TESTIMONIALS_COLLECTION);
        const newTestimonial = {
            ...testimonialData,
            date: testimonialData.date || new Date().toISOString(),
            status: testimonialData.status || 'pending',
            rating: testimonialData.rating || 5,
            featured: testimonialData.featured || false,
            createdAt: serverTimestamp(),
            updatedAt: serverTimestamp(),
        };

        const docRef = await addDoc(testimonialsRef, newTestimonial);
        console.log('✅ Testimonial added to Firebase:', docRef.id);
        return docRef.id;
    } catch (error) {
        console.error('❌ Firebase error, saving to localStorage:', error);
        // Fallback to localStorage
        const localTestimonials = getLocalTestimonials();
        const newId = Date.now().toString();
        const newItem = {
            ...testimonialData,
            id: newId,
            date: testimonialData.date || new Date().toISOString(),
            status: testimonialData.status || 'pending',
            rating: testimonialData.rating || 5,
            featured: testimonialData.featured || false,
            createdAt: new Date().toISOString(),
        } as Testimonial;
        localTestimonials.unshift(newItem);
        saveLocalTestimonials(localTestimonials);
        console.log('✅ Testimonial saved to localStorage:', newId);
        return newId;
    }
};

// Update existing testimonial
export const updateTestimonial = async (id: string, updates: Partial<Testimonial>): Promise<boolean> => {
    try {
        const docRef = doc(db, TESTIMONIALS_COLLECTION, id);
        await updateDoc(docRef, {
            ...updates,
            updatedAt: serverTimestamp(),
        });
        console.log('✅ Testimonial updated in Firebase:', id);
        return true;
    } catch (error) {
        console.error('❌ Error updating testimonial:', error);
        return false;
    }
};

// Delete testimonial
export const deleteTestimonial = async (id: string): Promise<boolean> => {
    try {
        const docRef = doc(db, TESTIMONIALS_COLLECTION, id);
        await deleteDoc(docRef);
        console.log('✅ Testimonial deleted from Firebase:', id);
        return true;
    } catch (error) {
        console.error('❌ Error deleting testimonial:', error);
        return false;
    }
};
